import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Modal from '../../shared/components/core-ui/modal/modal.component';
import { birdToast } from '../../shared/components/core-ui/toast';
import { STAFF_USER_TYPES } from '../../shared/components/group-ui/staff-picker/staff-picker.component';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { FinanceRequestPersonModel } from '../../shared/models/finance-request.model';
import {
  getFinanceReviewers,
  saveFinanceReviewers
} from '../../shared/services/data/finance-request.data';
import { searchUsers } from '../../shared/services/data/user.data';
import { UserUIModel } from '../../shared/ui-models/user.ui-model';
import { BTN_PRIMARY, BTN_SECONDARY, LABEL, SECTION_TITLE } from './finance-request.util';
import { selectStyles, SelectOption } from './request-select.component';

export const ReviewersModal: React.FC<{ onClose: () => void; onSaved: () => void }> = ({
  onClose,
  onSaved
}) => {
  const [staff, setStaff] = useState<UserUIModel[]>([]);
  const [selected, setSelected] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getFinanceReviewers(),
      searchUsers({ role: STAFF_USER_TYPES }, 'ReviewersModal')
    ]).then(([reviewers, users]) => {
      if (cancelled) return;
      if (isErrorInfo(reviewers)) {
        birdToast.error(reviewers.message || 'Could not load the reviewers.', 'Reviewers');
      } else {
        setSelected(reviewers.map((r) => ({ value: r.userId, label: r.name || r.userId })));
      }
      if (!isErrorInfo(users)) setStaff(users);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      staff
        .filter((u) => !!u.id)
        .map((u) => ({ value: u.id as string, label: u.name || u.email || (u.id as string) })),
    [staff]
  );

  const save = () => {
    setSaving(true);
    const reviewers: FinanceRequestPersonModel[] = selected.map((o) => ({
      userId: o.value,
      name: o.label
    }));
    saveFinanceReviewers(reviewers).then((result) => {
      setSaving(false);
      if (isErrorInfo(result)) {
        birdToast.error(result.message || 'Could not save the reviewers.', 'Reviewers');
        return;
      }
      birdToast.success('Reviewers saved', 'Reviewers');
      onSaved();
    });
  };

  return (
    <Modal
      config={{ showModal: true, fullScreen: false, showClose: true, maxWidth: 'sm' }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col gap-[20px] font-gilroy-regular text-neutral-500">
        <h2 className={`${SECTION_TITLE} !mb-0`}>Finance reviewers</h2>
        <div className={LABEL}>
          Reviewers
          <Select<SelectOption, true>
            aria-label="Reviewers"
            isMulti
            isLoading={loading}
            isDisabled={loading}
            value={selected}
            options={options}
            placeholder="Search staff"
            menuPortalTarget={document.body}
            menuPlacement="auto"
            styles={selectStyles}
            onChange={(value) => setSelected([...(value || [])])}
          />
        </div>
        <div className="flex justify-end gap-[6px]">
          <button type="button" className={BTN_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={BTN_PRIMARY} disabled={loading || saving} onClick={save}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};
